// The centre of the canvas: rows of twelve-column tiles you can drag and
// resize.
//
// **No drag-and-drop dependency.** Pointer events plus `setPointerCapture` are
// the whole mechanism, and they give the two properties a library would have
// been added for: a drag that keeps receiving moves after the cursor leaves the
// element, and touch support for free. What a library would ALSO have given is
// a layout model of its own, which this grid already has and does not want a
// second of.
//
// **Nothing here decides where a tile lands.** Every geometric answer comes
// from `lib/canvas-layout.ts`: the drop target from measured rectangles, the
// new width from a pixel delta against the grid width, the new address after a
// move. This component measures, calls, and paints. That split is what makes
// the off-by-one cases (a same-row drag to the right, a source row collapsing
// under the insertion point) assertable in a unit test instead of reproducible
// only with a mouse.
import { type PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from "react";

import type { ChartSpec, DashboardLayout, DatasetSpec, FilterState, LayoutTile } from "@bi/shared/types";

import {
  type DropTarget,
  type TileAddress,
  type TileRect,
  addressAfterDrop,
  applyDrop,
  heightFromDrag,
  resolveDropTarget,
  sameAddress,
  setTileHeight,
  setTileWidth,
  widthFromDrag,
} from "../../lib/canvas-layout";
import { columnSpans } from "../../lib/dashboard-layout";
import { isChartTile, isMarkdownTile } from "../../lib/studio-model";
import { Markdown } from "../markdown";
import { StudioPreviewTile } from "./studio-tile";

export interface CanvasGridProps {
  layout: DashboardLayout;
  charts: Readonly<Record<string, ChartSpec>>;
  datasets: Readonly<Record<string, DatasetSpec>>;
  filters: FilterState;
  employeeEmail: string;
  selected: TileAddress | null;
  onSelect: (address: TileAddress | null) => void;
  onLayoutChange: (layout: DashboardLayout, selected: TileAddress | null) => void;
  /** Why a tile with no rows is empty, per chart. Supplied by the page, which
   *  is the only layer that knows the experiment scope and the register. */
  emptyExplain?: (chart: ChartSpec) => string;
}

interface DragState {
  kind: "move" | "resize-width" | "resize-height";
  from: TileAddress;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
}

export function CanvasGrid({
  layout,
  charts,
  datasets,
  filters,
  employeeEmail,
  selected,
  onSelect,
  onLayoutChange,
  emptyExplain,
}: CanvasGridProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const tileRefs = useRef(new Map<string, HTMLElement>());
  const [drag, setDrag] = useState<DragState | null>(null);
  const [hoverTarget, setHoverTarget] = useState<DropTarget | null>(null);

  const registerTile = useCallback((address: TileAddress, node: HTMLElement | null) => {
    if (node) tileRefs.current.set(`${address.row}:${address.index}`, node);
    else tileRefs.current.delete(`${address.row}:${address.index}`);
  }, []);

  /** Every tile's box, in the canvas's own coordinate space. Measured at the
   *  START of a drag rather than on every move: the layout does not change
   *  while a tile is in the air, and re-measuring per pointermove is a forced
   *  reflow per frame. */
  const measure = useCallback((): TileRect[] => {
    const root = rootRef.current;
    if (!root) return [];
    const origin = root.getBoundingClientRect();
    const rects: TileRect[] = [];
    for (const [id, node] of tileRefs.current) {
      const [row, index] = id.split(":").map(Number);
      if (row === undefined || index === undefined) continue;
      const box = node.getBoundingClientRect();
      rects.push({
        row,
        index,
        left: box.left - origin.left,
        right: box.right - origin.left,
        top: box.top - origin.top,
        bottom: box.bottom - origin.top,
      });
    }
    return rects;
  }, []);
  const rectsRef = useRef<TileRect[]>([]);

  const startDrag = (
    event: ReactPointerEvent<HTMLElement>,
    kind: DragState["kind"],
    address: TileAddress,
    tile: LayoutTile,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    rectsRef.current = measure();
    onSelect(address);
    setDrag({
      kind,
      from: address,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: tile.width,
      startHeight: tile.height,
    });
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (!drag) return;
    if (drag.kind === "move") {
      const root = rootRef.current;
      if (!root) return;
      const origin = root.getBoundingClientRect();
      setHoverTarget(
        resolveDropTarget(rectsRef.current, {
          x: event.clientX - origin.left,
          y: event.clientY - origin.top,
        }),
      );
      return;
    }
    if (drag.kind === "resize-width") {
      const gridWidth = rootRef.current?.getBoundingClientRect().width ?? 0;
      const width = widthFromDrag(drag.startWidth, event.clientX - drag.startX, gridWidth);
      if (width !== (layout.rows[drag.from.row]?.tiles[drag.from.index]?.width ?? width)) {
        onLayoutChange(setTileWidth(layout, drag.from, width), drag.from);
      }
      return;
    }
    const height = heightFromDrag(drag.startHeight, event.clientY - drag.startY);
    if (height !== (layout.rows[drag.from.row]?.tiles[drag.from.index]?.height ?? height)) {
      onLayoutChange(setTileHeight(layout, drag.from, height), drag.from);
    }
  };

  const endDrag = () => {
    if (drag?.kind === "move" && hoverTarget) {
      const next = applyDrop(layout, drag.from, hoverTarget);
      // Follow the tile rather than searching for it: the selection has to land
      // on the thing that just moved, and deriving the address from the same
      // arithmetic the move used is the only way that stays true when a row
      // collapses under the insertion point.
      onLayoutChange(next, addressAfterDrop(layout, drag.from, hoverTarget));
    }
    setDrag(null);
    setHoverTarget(null);
  };

  return (
    <div
      className={`bi-studio-canvas${drag ? " bi-studio-canvas--dragging" : ""}`}
      ref={rootRef}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(e) => {
        if (e.target === e.currentTarget) onSelect(null);
      }}
    >
      {layout.rows.length === 0 ? (
        <p className="bi-studio-canvas__empty">
          Nothing here yet. Add a tile from the toolbar and pick the fields for it on the left.
        </p>
      ) : null}

      {layout.rows.map((row, rowIndex) => {
        const spans = columnSpans(row.tiles);
        const dropsAbove = hoverTarget?.kind === "newRow" && hoverTarget.row === rowIndex;
        return (
          <div key={rowIndex}>
            {dropsAbove ? <div className="bi-studio-droprule" aria-hidden="true" /> : null}
            <div className="bi-card-grid bi-studio-row">
              {row.tiles.map((tile, index) => {
                const address = { row: rowIndex, index };
                const isSelected = selected !== null && sameAddress(selected, address);
                const dropsBefore =
                  hoverTarget?.kind === "into" &&
                  hoverTarget.row === rowIndex &&
                  hoverTarget.index === index;
                const chart = isChartTile(tile) ? charts[tile.chart_uuid] : undefined;
                return (
                  <div
                    key={`${rowIndex}:${index}`}
                    ref={(node) => registerTile(address, node)}
                    className={[
                      "bi-studio-tile",
                      isSelected ? "bi-studio-tile--selected" : "",
                      dropsBefore ? "bi-studio-tile--dropbefore" : "",
                      drag?.kind === "move" && sameAddress(drag.from, address)
                        ? "bi-studio-tile--dragging"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={{ gridColumn: `span ${spans[index] ?? tile.width}` }}
                    onClick={() => onSelect(address)}
                  >
                    <button
                      type="button"
                      className="bi-studio-tile__grip"
                      aria-label={`Move ${isChartTile(tile) ? tile.slice_name : "text tile"}`}
                      onPointerDown={(e) => startDrag(e, "move", address, tile)}
                    >
                      Drag
                    </button>

                    {chart ? (
                      <StudioPreviewTile
                        chart={chart}
                        columnSpan={12}
                        width={tile.width}
                        dataset={datasets[chart.dataset_uuid]}
                        filters={filters}
                        employeeEmail={employeeEmail}
                        emptyExplanation={emptyExplain?.(chart)}
                      />
                    ) : isMarkdownTile(tile) ? (
                      <div className="bi-card bi-studio-text">
                        <Markdown source={tile.markdown} />
                      </div>
                    ) : (
                      <div className="bi-card bi-studio-text">
                        <p className="bi-card__empty">This tile references a chart that is gone.</p>
                      </div>
                    )}

                    <button
                      type="button"
                      className="bi-studio-tile__handle bi-studio-tile__handle--width"
                      aria-label={`Resize width, currently ${tile.width} of 12 columns`}
                      onPointerDown={(e) => startDrag(e, "resize-width", address, tile)}
                    />
                    <button
                      type="button"
                      className="bi-studio-tile__handle bi-studio-tile__handle--height"
                      aria-label={`Resize height, currently ${tile.height} units`}
                      onPointerDown={(e) => startDrag(e, "resize-height", address, tile)}
                    />
                  </div>
                );
              })}
              {hoverTarget?.kind === "into" &&
              hoverTarget.row === rowIndex &&
              hoverTarget.index >= row.tiles.length ? (
                <div className="bi-studio-dropslot" aria-hidden="true" />
              ) : null}
            </div>
            {hoverTarget?.kind === "newRow" && hoverTarget.row === rowIndex + 1 ? (
              <div className="bi-studio-droprule" aria-hidden="true" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
